/**
 * Wire contracts for the shelf-analytics module.
 *
 * The vision INPUT is deliberately loose (`z.record(z.unknown())`)
 * because the vision pipeline's schema changes upstream and we don't
 * want a validation error to gate ops from getting analytics. The
 * compute layer reads only the fields it cares about and reports
 * anything unusable via `diagnostics`.
 *
 * The OUTPUT is strict — every consumer (web dashboard, worker
 * rollups) benefits from a stable shape.
 */
import { z } from "zod";
// ── Config + rules (input) ─────────────────────────────────────────
export const healthWeights = z.object({
    empty_shelf: z.number().min(0).max(1),
    wrong_placement: z.number().min(0).max(1),
    wrong_orientation: z.number().min(0).max(1),
});
export const analyticsConfig = z.object({
    weights: healthWeights,
    front_threshold: z.number().min(0).max(1),
});
export const placementRule = z.object({
    shelf_id: z.number().int(),
    expected_shelf_id: z.number().int().optional(),
    allowed_category_ids: z.array(z.string()).optional(),
    allowed_sku_ids: z.array(z.string()).optional(),
    expected_x_start_ratio: z.number().min(0).max(1).optional(),
    expected_x_end_ratio: z.number().min(0).max(1).optional(),
    expected_sequence: z.array(z.string()).optional(),
});
// ── Output ─────────────────────────────────────────────────────────
export const componentScores = z.object({
    availability: z.number(),
    placement: z.number().nullable(),
    orientation: z.number(),
});
export const effectiveWeights = z.object({
    availability: z.number(),
    placement: z.number(),
    orientation: z.number(),
});
export const issueRates = z.object({
    empty_shelf_rate: z.number(),
    wrong_placement_rate: z.number().nullable(),
    wrong_orientation_rate: z.number(),
});
export const healthResult = z.object({
    score: z.number(),
    score_type: z.enum(["final", "provisional"]),
    component_scores: componentScores,
    effective_weights: effectiveWeights,
    issue_rates: issueRates,
});
export const countAnalysis = z.object({
    total_facings: z.number().int(),
    recognised_facings: z.number().int(),
    unknown_facings: z.number().int(),
    uncertain_facings: z.number().int(),
    front_facings: z.number().int(),
    wrong_orientation_facings: z.number().int(),
});
export const recognitionSummary = z.object({
    coverage_percent: z.number(),
    unknown_rate_percent: z.number(),
    uncertain_rate_percent: z.number(),
});
export const shareOfShelfEntry = z.object({
    entity_type: z.enum(["sku", "product", "brand", "category"]),
    entity_id: z.string(),
    entity_name: z.string(),
    count: z.object({
        entity_facings: z.number().int(),
        total_facings: z.number().int(),
        share_percent: z.number(),
    }),
    width: z.object({
        entity_occupied_width: z.number(),
        denominator_width: z.number(),
        gross_share_percent: z.number(),
        occupied_share_percent: z.number(),
    }),
});
export const shareOfShelfByEntity = z.object({
    by_sku: z.array(shareOfShelfEntry),
    by_product: z.array(shareOfShelfEntry),
    by_brand: z.array(shareOfShelfEntry),
    by_category: z.array(shareOfShelfEntry),
});
export const shelfAnalysis = z.object({
    shelf_id: z.number().int(),
    display_index: z.number().int().min(1),
    usable_shelf_width: z.number(),
    health: healthResult,
    count_analysis: countAnalysis,
    recognition: recognitionSummary,
    share_of_shelf: shareOfShelfByEntity,
});
export const fixtureAnalysis = z.object({
    usable_width: z.number(),
    oos_gap_width: z.number(),
    health: healthResult,
    count_analysis: countAnalysis,
    recognition: recognitionSummary,
    share_of_shelf: shareOfShelfByEntity,
});
export const analyticsOutput = z.object({
    configuration: analyticsConfig,
    fixture_analysis: fixtureAnalysis,
    shelf_analysis: z.array(shelfAnalysis),
    diagnostics: z.array(z.string()),
});
//# sourceMappingURL=shelf-analytics.schemas.js.map